/**
 * The one path from a mounted ticket card to its own rasterized image —
 * order-access.view.ts's share action and the designer's ticket-share studio
 * proof both call this, so "what the buyer sees" and "what gets shared" can
 * never drift: there is exactly one rasterizer, capturing the SAME live DOM
 * node `renderTicketVariantCard` mounted (modern-screenshot walks shadow
 * roots and bakes each element's computed style inline, so it captures what
 * the browser actually painted — schmancy-surface/schmancy-typography
 * internals included), scaled up from whatever CSS size the card actually
 * rendered at to `TICKET_CARD_IMAGE_WIDTH`'s own fixed width — never a
 * second, hand-rolled rendering of the ticket design. Bounded like every
 * other remote-ish wait on this surface: a hung rasterize rejects instead of
 * hanging its caller's spinner forever.
 */
import { domToBlob } from "modern-screenshot";
import { firstValueFrom, from, mergeMap, race, throwError, timer } from "rxjs";

/** The captured image's width in pixels — the card's own native 2:3 aspect
 *  ratio (`aspect-[2/3]` on every variant's root) determines the height, so
 *  no separate height constant is needed. */
export const TICKET_CARD_IMAGE_WIDTH = 1080;

/** Rasterizes `card` — the mounted card element `renderTicketVariantCard`
 *  produced — to a `TICKET_CARD_IMAGE_WIDTH`-wide PNG blob, scaled up from
 *  the card's own `getBoundingClientRect()` width. */
export function captureTicketCardImage(card: HTMLElement): Promise<Blob> {
	const width = card.getBoundingClientRect().width;
	if (width === 0)
		return Promise.reject(
			new Error("captureTicketCardImage: the ticket card has no layout yet"),
		);
	return firstValueFrom(
		race(
			from(
				domToBlob(card, {
					scale: TICKET_CARD_IMAGE_WIDTH / width,
					type: "image/png",
				}),
			),
			timer(15_000).pipe(
				mergeMap(() =>
					throwError(
						() => new Error("captureTicketCardImage: capture timed out"),
					),
				),
			),
		),
	);
}
